import { useState } from 'react'
import { useDomainManagement } from './useDomainManagement'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Loader2, Trash2 } from 'lucide-react'

interface DomainManagementPanelProps {
  enabled: boolean
  domain: string
  defaultSubdomain: string
  onRemove: () => void
  isRemoving: boolean
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function DomainManagementPanel({
  enabled,
  domain,
  defaultSubdomain,
  onRemove,
  isRemoving,
}: DomainManagementPanelProps) {
  const { data, isLoading, toggleAutoRenew, isToggling } = useDomainManagement(enabled)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)

  if (!enabled) return null

  if (isLoading || !data) {
    return (
      <div className="space-y-3 pt-2">
        <Separator />
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  const isPurchased = data.source === 'purchased'

  return (
    <div className="space-y-4 pt-2">
      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Source</span>
          <Badge variant={isPurchased ? 'default' : 'secondary'}>
            {isPurchased ? 'Purchased via Zaviago' : 'Connected externally'}
          </Badge>
        </div>

        {isPurchased && data.registered_at && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Registered</span>
            <span className="text-sm">{formatDate(data.registered_at)}</span>
          </div>
        )}

        {isPurchased && data.expires_at && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Expires</span>
            <span className="text-sm">{formatDate(data.expires_at)}</span>
          </div>
        )}

        {isPurchased && data.auto_renew !== null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Auto-renew</span>
            <Switch
              checked={data.auto_renew}
              onCheckedChange={(checked) => toggleAutoRenew(checked)}
              disabled={isToggling}
            />
          </div>
        )}
      </div>

      <Separator />

      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            className="rounded text-red-500 w-full justify-start"
            disabled={isRemoving}
          >
            {isRemoving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Remove Domain
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {domain}?</AlertDialogTitle>
            <AlertDialogDescription>
              {isPurchased
                ? `This will disconnect ${domain} from your storefront. The domain registration will remain active until it expires. Your store will revert to ${defaultSubdomain}.`
                : `This will disconnect ${domain} from your storefront. You will need to reconfigure DNS if you want to reconnect it later. Your store will revert to ${defaultSubdomain}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRemoveDialogOpen(false)
                onRemove()
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              Remove Domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
